import type { TimeTick } from "@/lib/chart/format"
import type { ChartTheme } from "@/lib/chart/theme"

/** Canvas drawing building blocks shared by the financial and series renderers. */

export const AXIS_FONT = "11px ui-monospace, SFMono-Regular, monospace"
export const LABEL_FONT = "10px ui-sans-serif, system-ui"
/** Minimum horizontal room per time-axis label. */
export const TIME_TICK_SPACING = 84

export interface PlotRect {
  left: number
  right: number
  top: number
  bottom: number
  width: number
  height: number
}

export function plotRect(fullWidth: number, left: number, rightMargin: number, top: number, bottom: number): PlotRect {
  const width = Math.max(1, fullWidth - left - rightMargin)
  return { left, right: left + width, top, bottom, width, height: Math.max(1, bottom - top) }
}

export function clearSurface(ctx: CanvasRenderingContext2D, theme: ChartTheme, width: number, height: number): void {
  ctx.clearRect(0, 0, width, height)
  ctx.fillStyle = theme.background
  ctx.fillRect(0, 0, width, height)
}

/** Horizontal lines on value ticks, vertical lines on the time ticks so the two line up. */
export function drawGrid(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  plot: PlotRect,
  valueTicks: number[],
  y: (value: number) => number,
  xPositions: number[],
): void {
  ctx.strokeStyle = theme.grid
  ctx.lineWidth = 1
  ctx.beginPath()
  for (const tick of valueTicks) {
    const position = Math.round(y(tick)) + 0.5
    ctx.moveTo(plot.left, position)
    ctx.lineTo(plot.right, position)
  }
  for (const x of xPositions) {
    const position = Math.round(x) + 0.5
    ctx.moveTo(position, plot.top)
    ctx.lineTo(position, plot.bottom)
  }
  ctx.stroke()
}

export function drawValueAxis(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  plot: PlotRect,
  ticks: number[],
  y: (value: number) => number,
  format: (value: number) => string,
  unit?: string,
): void {
  ctx.fillStyle = theme.text
  ctx.font = AXIS_FONT
  ctx.textAlign = "start"
  ctx.textBaseline = "middle"
  for (const tick of ticks) ctx.fillText(format(tick), plot.right + 8, y(tick))
  if (unit) {
    // The unit is drawn once as an axis title rather than repeated on every tick.
    ctx.font = LABEL_FONT
    ctx.textBaseline = "top"
    ctx.fillText(unit, plot.right + 8, 2, 62)
  }
}

export function drawTimeAxis(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  plot: PlotRect,
  ticks: TimeTick[],
  x: (index: number) => number,
): void {
  ctx.fillStyle = theme.text
  ctx.font = LABEL_FONT
  ctx.textAlign = "center"
  ctx.textBaseline = "top"
  for (const tick of ticks) ctx.fillText(tick.label, x(tick.index), plot.bottom + 7)
  ctx.textAlign = "start"
}

interface CrosshairOptions {
  x: number
  /** Pointer y; the horizontal line and value label are drawn only inside `plot`. */
  y: number | null
  plot: PlotRect
  /** Bottom of the vertical line (below any lower panes). */
  bottom: number
  width: number
  timeLabel: string
  valueLabel: string | null
}

export function drawCrosshair(ctx: CanvasRenderingContext2D, theme: ChartTheme, options: CrosshairOptions): void {
  const { x, y, plot, bottom, width, timeLabel, valueLabel } = options
  ctx.strokeStyle = theme.crosshair
  ctx.lineWidth = 1
  ctx.setLineDash([4, 4])
  ctx.beginPath()
  ctx.moveTo(x, plot.top)
  ctx.lineTo(x, bottom)
  const showValue = y !== null && valueLabel !== null && y >= plot.top && y <= plot.bottom
  if (showValue) {
    ctx.moveTo(plot.left, y)
    ctx.lineTo(plot.right, y)
  }
  ctx.stroke()
  ctx.setLineDash([])

  ctx.font = AXIS_FONT
  ctx.textBaseline = "middle"
  const timeWidth = ctx.measureText(timeLabel).width + 12
  const timeX = Math.max(plot.left, Math.min(width - timeWidth - 2, x - timeWidth / 2))
  drawLabel(ctx, theme, timeLabel, timeX, bottom + 2, timeWidth)
  if (showValue) {
    drawLabel(ctx, theme, valueLabel, plot.right + 2, y - 9, Math.max(1, width - plot.right - 4))
  }
}

function drawLabel(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  text: string,
  left: number,
  top: number,
  width: number,
): void {
  ctx.fillStyle = theme.crosshairLabelBackground
  ctx.fillRect(left, top, width, 18)
  ctx.fillStyle = theme.crosshairLabelText
  ctx.textAlign = "start"
  ctx.fillText(text, left + 6, top + 9, width - 8)
}

export function drawLine(
  ctx: CanvasRenderingContext2D,
  points: Array<{ x: number; y: number | null }>,
  color: string,
  width: number,
): void {
  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.lineJoin = "round"
  ctx.beginPath()
  let drawing = false
  for (const point of points) {
    if (point.y === null || !Number.isFinite(point.y)) {
      drawing = false
    } else if (drawing) {
      ctx.lineTo(point.x, point.y)
    } else {
      ctx.moveTo(point.x, point.y)
      drawing = true
    }
  }
  ctx.stroke()
}

/** Pane caption plus its top value, for the volume/open-interest panes. */
export function drawPaneLabel(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  pane: PlotRect,
  label: string,
  maxValue: string | null,
): void {
  ctx.fillStyle = theme.text
  ctx.font = LABEL_FONT
  ctx.textAlign = "start"
  ctx.textBaseline = "middle"
  ctx.fillText(label, pane.left + 4, pane.top + 12)
  if (maxValue !== null) {
    ctx.font = AXIS_FONT
    ctx.fillText(maxValue, pane.right + 8, pane.top + 18)
  }
}

export function drawEmptyState(
  ctx: CanvasRenderingContext2D,
  theme: ChartTheme,
  width: number,
  height: number,
  message: string,
): void {
  ctx.fillStyle = theme.text
  ctx.font = "14px ui-sans-serif, system-ui"
  ctx.textAlign = "center"
  ctx.textBaseline = "middle"
  ctx.fillText(message, width / 2, height / 2)
  ctx.textAlign = "start"
}
